import { useQuery } from '@tanstack/react-query';
import { dashboardApi, type DashboardAccessContext } from '@/api/endpoints/dashboard';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Shared cache key for the BASE-tier / Limited-User dispatcher data.
 * Scoped by `userId` so a prior session cannot serve stale tier data after
 * login, impersonation, or remember-me resume.
 */
export function dashboardAccessContextQueryKey(userId: number) {
  return ['dashboard', 'access-context', userId] as const;
}

export function useDashboardAccessContext() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasToken = useAuthStore((s) => Boolean(s.accessToken));
  const userId = useAuthStore((s) => s.user?.userId ?? 0);
  const queryEnabled = isAuthenticated && hasToken && userId > 0;
  return useQuery<DashboardAccessContext>({
    queryKey: dashboardAccessContextQueryKey(userId),
    queryFn: () => dashboardApi.getAccessContext(),
    staleTime: 0,
    refetchOnMount: 'always',
    enabled: queryEnabled,
  });
}

/**
 * True when access-context has resolved and the dashboard gate is off for this
 * session. SSI staff always receive `dashboardEnabled: true` from the API
 * (tier gate bypass). Returns false while loading so nav does not flicker.
 */
export function useDashboardNavHidden(): boolean {
  const { data, isSuccess } = useDashboardAccessContext();
  if (!isSuccess || !data) return false;
  return !data.dashboardEnabled;
}

/**
 * True when Drawing Vault nav should hide — grant-driven, not TruSpace CBS tier.
 * SSI staff always receive `drawingVaultEnabled: true`. Returns false while loading.
 */
export function useDrawingVaultNavHidden(): boolean {
  const { data, isSuccess } = useDashboardAccessContext();
  if (!isSuccess || !data) return false;
  return !data.drawingVaultEnabled;
}

/** @deprecated Prefer `useDashboardNavHidden` — tier alone mis-gates SSI staff. */
export function useDashboardIsBaseTier(): boolean {
  return useDashboardNavHidden();
}
